// components/GastSteinDetail.js
// Dies ist eine angepasste Version von SteinDetail.js für Gast-Benutzer (nur Ansicht)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import Modal from './Modal';
import SteinBild from './SteinBild';
import '../styles/SteinDetail.css';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Ggf. nicht mehr benötigt
// import { faArrowLeft, faSave, faTimes, faTrashAlt, faImage } from '@fortawesome/free-solid-svg-icons'; // Ggf. nicht mehr benötigt

const attributeDisplayNames = {
    name: 'Name', // Wird meist im Titel angezeigt
    bildPfad: 'Bild', // Wird als Bild angezeigt
    kategorie: 'Kategorie',
    farbe: 'Farbe',
    status: 'Status',
    gewicht: 'Gewicht',
    preisProGramm: 'Preis/Gramm',
    durchmesser: 'Durchmesser',
    laenge: 'Länge',
    breite: 'Breite',
    istSerie: 'Teil einer Serie',
    serienBezeichnung: 'Serienbezeichnung',
    kaufPreis: 'Kaufpreis',
    kaufDatum: 'Kaufdatum',
    kaufOrt: 'Kaufort',
    verkaeufer: 'Verkäufer',
    verkaufPreis: 'Verkaufspreis',
    verkaufDatum: 'Verkaufsdatum',
    verkaufOrt: 'Verkaufsort',
    kaeufer: 'Käufer',
    notizen: 'Notizen',
  };

// Hilfsfunktion zum Rendern eines Detailfeldes, angepasst an SteinDetail.js Logik
function renderDetailField(label, value, attributeKey = '') {
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
        return null;
    }

    let displayValue = String(value);
    const currentAttributeKey = attributeKey || (Object.keys(attributeDisplayNames).find(k => attributeDisplayNames[k] === label) || label.toLowerCase());

    if (currentAttributeKey === 'gewicht' && typeof value === 'number') displayValue = `${value} g`;
    else if (['durchmesser', 'laenge', 'breite'].includes(currentAttributeKey) && typeof value === 'number') displayValue = `${value} mm`;
    else if (['kaufPreis', 'verkaufPreis', 'preisProGramm'].includes(currentAttributeKey) && typeof value === 'number') displayValue = `€ ${value.toFixed(2)}`;
    else if ((currentAttributeKey === 'kaufDatum' || currentAttributeKey === 'verkaufDatum') && value) {
        try {
            displayValue = new Date(value).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch (e) { /* bleibt Originalwert */ }
    }
    else if (currentAttributeKey === 'istSerie') displayValue = value ? 'Ja' : 'Nein';

    return (
        <p key={`${label}-${currentAttributeKey}`}>
            <strong>{label}:</strong> {displayValue}
        </p>
    );
}


function GastSteinDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [stein, setStein] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);

    const fetchStein = useCallback(() => {
        setIsLoading(true);
        setError(null);
        API.get(`/steine/${id}`)
            .then(res => {
                setStein(res.data);
            })
            .catch(err => {
                console.error("Fehler beim Laden des Steins (Gast):", err);
                if (err.response && (err.response.status === 401 || (err.response.status === 400 && err.response.data?.message === 'Ungültiges oder abgelaufenes Token.') || (err.response.status === 400 && err.response.data === 'Ungültiges Token'))) {
                    setError('Sitzung abgelaufen oder Token ungültig. Bitte neu anmelden.');
                } else if (err.response && err.response.status === 404) {
                    setError('Stein nicht gefunden oder für Sie nicht freigegeben.');
                } else if (err.response && err.response.status === 403) { 
                    setError('Sie haben keine Berechtigung, diesen Stein anzuzeigen.');
                } else {
                    setError('Fehler beim Laden der Steindetails.');
                }
                setStein(null); 
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    useEffect(() => {
        fetchStein();
    }, [fetchStein]);

    const handleGoBack = () => {
        if (window.history.length > 1 && document.referrer.includes(window.location.host)) {
            navigate(-1);
        } else {
            navigate('/'); 
        }
    };

    if (isLoading) {
        return <div className="loading-indicator"><p>Lade Steindetails...</p></div>;
    }

    if (error) {
        return (
            <div className="error-message error-container">
                <p>{error}</p>
                <button onClick={handleGoBack} className="btn">Zurück zur Liste</button>
                {!(error.includes("anmelden") || error.includes("Token") || error.includes("Berechtigung")) &&
                    <button onClick={fetchStein} className="btn btn-retry">Erneut versuchen</button>}
            </div>
        );
    }

    if (!stein) {
        return (
             <div className="error-message error-container">
                <p>Stein nicht gefunden oder konnte nicht geladen werden.</p>
                <button onClick={handleGoBack} className="btn">Zurück zur Liste</button>
            </div>
        );
    }


    // Definiere, welche Attribute in welchen Sektionen angezeigt werden sollen
    const basisDetailsKeys = ['kategorie', 'farbe', 'status', 'durchmesser', 'laenge', 'breite', 'gewicht', 'preisProGramm', 'istSerie', 'serienBezeichnung'];
    const kaufDetailsKeys = ['kaufPreis', 'kaufDatum', 'kaufOrt', 'verkaeufer'];
    const verkaufDetailsKeys = ['verkaufPreis', 'verkaufDatum', 'verkaufOrt', 'kaeufer'];
    const notizenKey = 'notizen';

    // Filter die anzuzeigenden Keys basierend auf dem, was im Stein-Objekt vorhanden ist (und nicht null/leer ist)
    const getDisplayableDetails = (keys) => {
        return keys.filter(key => stein[key] !== null && stein[key] !== undefined && String(stein[key]).trim() !== '');
    };

    const displayableBasisDetails = getDisplayableDetails(basisDetailsKeys);
    const displayableKaufDetails = getDisplayableDetails(kaufDetailsKeys);
    const displayableVerkaufDetails = getDisplayableDetails(verkaufDetailsKeys);
    const displayableNotizen = stein[notizenKey] && String(stein[notizenKey]).trim() !== '';

    return (
        <main className="stein-detail-container gast-detail-container">
            <div className="navigation-header">
                <button onClick={handleGoBack} className="btn btn-back">
                    <span>Zurück zur Liste</span>
                </button>
            </div>
            
            <div className="stein-content-layout">
                {stein.bildPfad && (
                    <SteinBild
                        bildPfad={stein.bildPfad}
                        size="detail"
                        onClick={() => setShowImageModal(true)}
                        alt={stein.name || 'Steinbild'}
                    />
                )}

                {/* Verwendet .stein-info für den Textbereich wie in SteinDetail.js */} 
                <div className="stein-info">
                    {stein.name && <h2 className="stein-title-detail-page">{stein.name}</h2>} 

                    {/* Basis Details direkt rendern */}
                    {displayableBasisDetails.map(key => renderDetailField(attributeDisplayNames[key] || key, stein[key], key))}

                    {/* Kaufdetails Sektion */} 
                    {displayableKaufDetails.length > 0 && (
                        <div className="info-section">
                            <h3>Kaufdetails</h3>
                            {displayableKaufDetails.map(key => renderDetailField(attributeDisplayNames[key] || key, stein[key], key))}
                        </div>
                    )}

                    {/* Verkaufsdetails Sektion */} 
                    {displayableVerkaufDetails.length > 0 && (
                        <div className="info-section">
                            <h3>Verkaufsdetails</h3>
                            {displayableVerkaufDetails.map(key => renderDetailField(attributeDisplayNames[key] || key, stein[key], key))}
                        </div>
                    )}

                    {/* Notizen Sektion */} 
                    {displayableNotizen && (
                        <div className="info-section">
                            <h3>Notizen</h3>
                            <p className="notizen-text">{stein.notizen}</p>
                        </div>
                    )}
                </div>
            </div>

            {showImageModal && stein.bildPfad && (
                <Modal onClose={() => setShowImageModal(false)}>
                    <img src={`${process.env.REACT_APP_BASE_URL}/${stein.bildPfad}`} alt={`${stein.name || 'Stein'} Großansicht`} className="modal-image" />
                </Modal>
            )}
        </main>
    );
}

export default GastSteinDetail; 